import { useState, useCallback, useEffect } from 'react';
import { isValidStateSlug } from '../utils/stateUtils';

interface GeoLocation {
  state?: string;
}

/**
 * Manages the "Set your location" prompt visibility:
 * - Shows the prompt once per hour (throttled via localStorage) when the user
 *   has no state in their URL or geo context.
 * - Auto-dismisses when the user sets their location.
 */
export function useLocationPrompt(state: string | undefined, location: GeoLocation | null) {
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  // Decide whether to show the prompt
  useEffect(() => {
    if (!location?.state && !isValidStateSlug(state)) {
      const sessionDismissed = sessionStorage.getItem('locationPromptDismissed');
      if (sessionDismissed) return;

      const lastPromptTime = localStorage.getItem('locationPromptTime');
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      const shouldShow = !lastPromptTime || now - parseInt(lastPromptTime, 10) > oneHour;

      if (shouldShow) {
        const timer = setTimeout(() => {
          setShowLocationPrompt(true);
          localStorage.setItem('locationPromptTime', now.toString());
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- country intentionally excluded
  }, [location, state]);

  // Auto-dismiss when user sets their location
  useEffect(() => {
    if (location?.state && showLocationPrompt) {
      setShowLocationPrompt(false);
    }
  }, [location?.state, showLocationPrompt]);

  const handleDismissPrompt = useCallback(() => {
    setShowLocationPrompt(false);
    localStorage.setItem('locationPromptTime', Date.now().toString());
    sessionStorage.setItem('locationPromptDismissed', 'true');
  }, []);

  return { showLocationPrompt, handleDismissPrompt };
}
